import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import { DiscountKind, DiscountStatus, type Discount } from '@nucleus-commerce/api-client/admin';

/** Fields GET /discounts sorts on; anything else is a 400. */
export const DISCOUNT_SORTS = ['title', 'startsAt', 'usageCount'] as const;

/** `appliesTo.scope` values; the API filters on them but publishes no enum model. */
export const DISCOUNT_SCOPES = ['order', 'products', 'collections'] as const;

const valuesOf = (values: readonly string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const DISCOUNT_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(Object.values(DiscountStatus)) },
  { key: 'kind', type: 'enum', options: valuesOf(Object.values(DiscountKind)) },
  { key: 'scope', type: 'enum', options: valuesOf(DISCOUNT_SCOPES) },
  { key: 'startsAt', type: 'date-range' },
] as const;

/** Filters of the discounts table, in the order the filter menu lists them. `t` is `discounts`. */
export function discountFilters(t: TFunction): Array<FilterDef<Discount>> {
  const options = (values: readonly string[], prefix: string): FilterOption[] =>
    values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(Object.values(DiscountStatus), 'status'),
    },
    {
      key: 'kind',
      label: t('filters.kind'),
      type: 'enum',
      pinned: true,
      options: options(Object.values(DiscountKind), 'kind'),
    },
    {
      key: 'scope',
      label: t('filters.scope'),
      type: 'enum',
      aliases: ['appliesTo'],
      options: options(DISCOUNT_SCOPES, 'scope'),
    },
    { key: 'startsAt', label: t('filters.startsAt'), type: 'date-range', aliases: ['starts'] },
  ];
}
